import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FaStar } from 'react-icons/fa';
import './TenantRatingModal.css';
import { rateTenant } from '../../../api/services/tenantService';

const TenantRatingModal = ({ isOpen, onClose, tenant, propertyId, unitId, onRatingSuccess }) => {
  const [score, setScore] = useState(5);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [hoverScore, setHoverScore] = useState(0);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // UUID regex — real user IDs look like xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    const isUUID = (val) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(val || ''));

    // Resolve tenantUserId — prefer explicit user UUIDs over tenant codes (e.g. TENANT-XXXXX)
    const tenantUserId =
      (isUUID(tenant?.tenantUserId) && tenant?.tenantUserId) ||
      (isUUID(tenant?.userId) && tenant?.userId) ||
      (isUUID(tenant?.tenant?.id) && tenant?.tenant?.id) ||
      (isUUID(tenant?.tenant?.userId) && tenant?.tenant?.userId) ||
      (isUUID(tenant?.tenantId) && tenant?.tenantId) ||
      null;

    if (!tenantUserId) {
      setError('Could not resolve tenant user ID. The unit may not have a linked user account.');
      console.error('[TenantRatingModal] No valid UUID found. tenant object:', tenant);
      return;
    }

    const finalMessage = message.trim() || 'Excellent tenant, paid on time and kept the place clean.';

    const payload = {
      propertyId,
      score,
      message: finalMessage,
    };

    // Debug: log what we're sending so you can verify in browser console
    console.log('[TenantRatingModal] Submitting rating:', {
      tenantUserId,
      tenantObject: tenant,
      payload,
    });

    setSubmitting(true);
    setError('');

    try {
      const response = await rateTenant(tenantUserId, payload);

      if (response.status === 'success') {
        if (onRatingSuccess) onRatingSuccess(response.message || 'Tenant rated successfully');
        onClose();
      } else {
        setError(response.message || 'Failed to rate tenant');
      }
    } catch (err) {
      console.error('[TenantRatingModal] Rating error:', err);
      setError(err.message || 'An error occurred while submitting the rating');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="tenant-rating-modal-overlay" onClick={onClose}>
      <div className="tenant-rating-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="tenant-rating-modal-header">
          <h2>Rate Tenant</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="tenant-rating-modal-form">
          <div className="tenant-info-summary">
            <label>Tenant:</label>
            <span>{tenant?.tenantName || 'Tenant'}</span>
          </div>
          
          <div className="rating-stars-container">
            <label>Rating Score:</label>
            <div className="stars-wrapper">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  className={`rating-star ${(hoverScore || score) >= star ? 'active' : ''}`}
                  onMouseEnter={() => setHoverScore(star)}
                  onMouseLeave={() => setHoverScore(0)}
                  onClick={() => setScore(star)}
                />
              ))}
            </div>
            <span className="score-label">{score}/5</span>
          </div>

          <div className="form-group">
            <label>Feedback Message:</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Excellent tenant, paid on time and kept the place clean."
              rows="4"
            />
          </div>

          {error && <div className="rating-error-message">{error}</div>}

          <div className="tenant-rating-modal-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Rating'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default TenantRatingModal;
